import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isRTL = i18n.language?.startsWith("ar");
  const whatsappMessage = isRTL
    ? "مرحباً، أرغب في طلب عرض سعر من ابراج الرفوف."
    : "Hello, I would like to request a quote from Arafouf.";
  const whatsappLink = `https://wa.me/966532785554?text=${encodeURIComponent(whatsappMessage)}`;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const toggleLanguage = () => {
    const currentLang = i18n.language || "ar";
    const newLang = currentLang.startsWith("ar") ? "en" : "ar";
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang.startsWith("ar") ? "rtl" : "ltr";
    document.documentElement.lang = newLang;
    navigate(location.pathname);
  };

  const navItems = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.services"), path: "/services" },
    { label: t("nav.about"), path: "/about-us" },
    { label: t("nav.contact"), path: "/contact-us" },
  ];
  const logoSrc = "/assets/logos/logo_abarj.png";

  const isHome = location.pathname === "/";

  const menuVariants = {
    hidden: {
      x: isRTL ? "100%" : "-100%",
      opacity: 0,
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
    exit: {
      x: isRTL ? "100%" : "-100%",
      opacity: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1],
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      x: isRTL ? 80 : -80,
      opacity: 0,
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
    exit: {
      x: isRTL ? 40 : -40,
      opacity: 0,
      transition: {
        duration: 0.25,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const closeVariants = {
    hidden: { rotate: -90, opacity: 0, scale: 0.5 },
    visible: {
      rotate: 0,
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
    },
    exit: {
      rotate: 90,
      opacity: 0,
      scale: 0.5,
      transition: { duration: 0.25 },
    },
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHome
            ? "bg-white/95 backdrop-blur-sm shadow-sm border-b border-stone-200"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
              className="inline-flex items-center"
            >
              <img
                src={logoSrc}
                alt="Abraj Al-Rafouf"
                className={`w-auto object-contain transition-all duration-300 ${
                  isScrolled || !isHome
                    ? "h-10 md:h-12"
                    : "h-11 md:h-14 drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
                }`}
                loading="eager"
                decoding="async"
              />
            </a>

            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.path}
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.path);
                  }}
                  className={`desktop-nav-link font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    location.pathname === item.path
                      ? isScrolled || !isHome
                        ? "text-brand font-semibold"
                        : "text-brand font-semibold"
                      : isScrolled || !isHome
                        ? "text-stone-600 hover:text-stone-900"
                        : "text-white/80 hover:text-white"
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className={`text-sm md:text-base font-medium transition-colors whitespace-nowrap cursor-pointer px-2 py-1 rounded-md ${
                  isScrolled || !isHome
                    ? "text-stone-600 hover:text-stone-900 hover:bg-stone-100"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {isRTL ? "EN" : "AR"}
              </button>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`hidden sm:inline-flex items-center gap-2 text-sm md:text-base font-medium px-5 py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  isScrolled || !isHome
                    ? "bg-brand text-white hover:bg-brand-dark"
                    : "bg-white text-brand hover:bg-white/90"
                }`}
              >
                {t("nav.requestQuote")}
                <span className="w-5 h-5 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-whatsapp-line text-xs" />
                </span>
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                  isScrolled || !isHome
                    ? "text-stone-900 hover:bg-stone-100"
                    : "text-white hover:bg-white/10"
                }`}
              >
                <i className={`ri-${mobileMenuOpen ? "close-line" : "menu-line"} text-xl`} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-white"
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            dir={isRTL ? "rtl" : "ltr"}
          >
            {/* Header bar */}
            <div className="flex items-center justify-between h-16 px-4 flex-shrink-0 border-b border-stone-100">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/");
                  setMobileMenuOpen(false);
                }}
                className="inline-flex items-center"
              >
                <img
                  src={logoSrc}
                  alt="Abraj Al-Rafouf"
                  className="h-10 w-auto object-contain"
                  loading="eager"
                  decoding="async"
                />
              </a>
              <motion.button
                onClick={() => setMobileMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-stone-100 text-stone-900 cursor-pointer"
                variants={closeVariants}
              >
                <i className="ri-close-line text-xl" />
              </motion.button>
            </div>

            {/* Menu items */}
            <div className="flex-1 flex flex-col justify-center px-6 md:px-10 py-8">
              <nav className="space-y-2">
                {navItems.map((item, index) => (
                  <motion.a
                    key={item.path}
                    href={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                    variants={itemVariants}
                    className={`group flex items-center ${isRTL ? "justify-end" : "justify-start"} gap-3 py-4 cursor-pointer border-b border-stone-100`}
                  >
                    <span
                      className={`text-sm font-medium text-brand transition-transform duration-300 group-hover:scale-110 ${
                        isRTL ? "order-2" : "order-1"
                      }`}
                    >
                      0{index + 1}
                    </span>
                    <span
                      className={`text-2xl md:text-3xl font-bold transition-colors duration-300 ${
                        location.pathname === item.path
                          ? "text-brand"
                          : "text-stone-900 group-hover:text-brand"
                      }`}
                    >
                      {item.label}
                    </span>
                  </motion.a>
                ))}
              </nav>
            </div>

            {/* Bottom CTA */}
            <motion.div
              variants={itemVariants}
              className="flex-shrink-0 px-6 md:px-10 pb-8 pt-4"
            >
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setMobileMenuOpen(false);
                }}
                className={`w-full inline-flex items-center ${isRTL ? "flex-row-reverse" : ""} justify-center gap-3 bg-brand text-white text-lg font-semibold px-8 py-4 rounded-full hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer`}
              >
                {t("nav.requestQuote")}
                <span className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-whatsapp-line text-sm" />
                </span>
              </a>

              <div className={`flex items-center ${isRTL ? "justify-end" : "justify-start"} gap-4 mt-6`}>
                <button
                  onClick={() => {
                    toggleLanguage();
                    setMobileMenuOpen(false);
                  }}
                  className="text-sm font-medium text-stone-500 hover:text-stone-900 transition-colors cursor-pointer"
                >
                  {isRTL ? "English" : "العربية"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}